import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Star, Home, PartyPopper, CheckCircle2, Loader2, Award } from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useCourse } from '@/hooks/useCourses'
import { useSubmitFeedback, useMyFeedback } from '@/hooks/useFeedback'

const STAR_LABELS = ['Ruim', 'Regular', 'Bom', 'Muito bom', 'Excelente']

const CourseFeedback = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const cId = parseInt(courseId || '0')

  const { data: course } = useCourse(cId)
  const { data: existingFeedback } = useMyFeedback(cId)
  const submitFeedback = useSubmitFeedback()

  const [showForm, setShowForm] = useState(false)
  const [rating, setRating] = useState<number>(0)
  const [hovered, setHovered] = useState<number>(0)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowForm(true), 3000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (existingFeedback) {
      setRating(existingFeedback.rating)
      setComment(existingFeedback.comment)
    }
  }, [existingFeedback])

  const handleSubmit = () => {
    if (rating === 0 || comment.trim().length < 50) return
    submitFeedback.mutate(
      { courseId: cId, rating, comment: comment.trim() },
      { onSuccess: () => setSubmitted(true) }
    )
  }

  const activeStars = hovered || rating
  const courseName = course?.title ?? 'Jornada IA'

  const gradientBase = 'linear-gradient(to bottom, #ff3200 0%, hsl(var(--background)) 100%)'
  const gradientDark = 'linear-gradient(to bottom, #a01e00 0%, hsl(var(--background)) 100%)'

  // ── Já avaliou (ou acabou de enviar) — mostra apenas celebração ────────────
  if (existingFeedback || submitted) {
    return (
      <div className="relative min-h-screen flex flex-col">
        <div className="absolute inset-0" style={{ background: gradientBase }} />
        <div className="relative z-10 flex flex-col min-h-screen">
          <Header />
          <main className="flex-1 container flex flex-col items-center justify-center py-10 gap-6">
            <motion.div
              initial={{ scale: 0, rotate: -15 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 12 }}
              className="mb-2"
            >
              <PartyPopper className="w-20 h-20 text-white" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl font-bold text-white"
            >
              Parabéns!
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="text-lg text-white/80"
            >
              Você concluiu o curso <span className="text-white font-semibold">{courseName}</span>.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="text-base text-white/90 max-w-md"
            >
              Obrigado pelo seu feedback, aqui está o seu certificado!
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="flex flex-col sm:flex-row gap-3 mt-4"
            >
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="gap-2 bg-white text-orange-600 hover:bg-white/90"
              >
                <Link to={`/perfil?tab=certificados&courseId=${cId}`}>
                  <Award className="w-4 h-4" />
                  Emitir Certificado
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="gap-2 border-white/40 text-white hover:bg-white/10"
              >
                <Link to="/">
                  <Home className="w-4 h-4" />
                  Voltar ao Início
                </Link>
              </Button>
            </motion.div>
          </main>
          <Footer />
        </div>
      </div>
    )
  }

  // ── Tela principal (fase 1 e fase 2 unificadas) ─────────────────────────────
  return (
    <div className="relative min-h-screen flex flex-col">
      {/* Gradiente base */}
      <div className="absolute inset-0" style={{ background: gradientBase }} />

      {/* Gradiente escuro que entra ao exibir o formulário */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: showForm ? 1 : 0 }}
        transition={{ duration: 1.2 }}
        style={{ background: gradientDark }}
      />

      <div className="relative z-10 flex flex-col min-h-screen">
        <Header />

        <main className="flex-1 container flex flex-col items-center justify-center py-10 gap-8">
          {/* ── Bloco de celebração ── */}
          <motion.div layout className="flex flex-col items-center text-center">
            {/* Ícone */}
            <motion.div
              layout
              initial={{ scale: 0, rotate: -15 }}
              animate={{
                scale: 1,
                rotate: 0,
                width: showForm ? 48 : 80,
                height: showForm ? 48 : 80,
              }}
              transition={{
                scale: { type: 'spring', stiffness: 200, damping: 12 },
                rotate: { type: 'spring', stiffness: 200, damping: 12 },
                width: { duration: 0.6, ease: 'easeInOut' },
                height: { duration: 0.6, ease: 'easeInOut' },
              }}
              className={showForm ? 'mb-4' : 'mb-6'}
            >
              <PartyPopper className="w-full h-full text-white" />
            </motion.div>

            {/* Título */}
            <motion.h1
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{
                opacity: 1,
                y: 0,
                fontSize: showForm ? '1.5rem' : '2.5rem',
              }}
              transition={{
                opacity: { delay: 0.3 },
                y: { delay: 0.3 },
                fontSize: { duration: 0.6, ease: 'easeInOut' },
              }}
              className="font-bold mb-2 text-white"
            >
              Parabéns!
            </motion.h1>

            {/* Subtítulo */}
            <motion.p
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{
                opacity: 1,
                y: 0,
                fontSize: showForm ? '0.875rem' : '1.125rem',
              }}
              transition={{
                opacity: { delay: 0.55 },
                y: { delay: 0.55 },
                fontSize: { duration: 0.6, ease: 'easeInOut' },
              }}
              className="text-white/80"
            >
              Você concluiu o curso <span className="text-white font-semibold">{courseName}</span>.
            </motion.p>

            {/* Escadinha de quadradinhos — sai ao exibir o formulário */}
            <AnimatePresence>
              {!showForm && (
                <motion.div
                  className="flex items-end justify-center gap-2 mt-10"
                  initial="hidden"
                  animate="visible"
                  exit={{ opacity: 0, transition: { duration: 0.35 } }}
                >
                  {[1, 2, 3].map((count, colIndex) => (
                    <motion.div
                      key={colIndex}
                      className="flex flex-col-reverse gap-2"
                      variants={{
                        hidden: {},
                        visible: {
                          transition: {
                            staggerChildren: 0.12,
                            delayChildren: 0.8 + colIndex * 0.45,
                          },
                        },
                      }}
                    >
                      {Array.from({ length: count }).map((_, i) => (
                        <motion.div
                          key={i}
                          className="w-6 h-6 rounded-sm bg-white/70"
                          variants={{
                            hidden: { opacity: 0, scale: 0 },
                            visible: {
                              opacity: 1,
                              scale: 1,
                              transition: { type: 'spring', stiffness: 260, damping: 18 },
                            },
                          }}
                        />
                      ))}
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* ── Card de avaliação — entra após os 3s ── */}
          <AnimatePresence>
            {showForm && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: 'easeOut', delay: 0.45 }}
                className="w-full max-w-lg"
              >
                <div className="bg-card border border-border rounded-2xl p-8 shadow-sm space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-1">
                      Avalie este curso
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Sua opinião nos ajuda a melhorar a experiência de aprendizado.
                    </p>
                  </div>

                  {/* Estrelas */}
                  <div>
                    <Label className="text-sm font-medium mb-3 block">Nota geral</Label>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          onMouseEnter={() => setHovered(star)}
                          onMouseLeave={() => setHovered(0)}
                          className="transition-transform hover:scale-110 focus:outline-none"
                          aria-label={`${star} estrela${star > 1 ? 's' : ''}`}
                        >
                          <Star
                            className={`w-9 h-9 transition-colors ${
                              star <= activeStars
                                ? 'fill-primary text-primary'
                                : 'text-muted-foreground/40'
                            }`}
                          />
                        </button>
                      ))}
                      {activeStars > 0 && (
                        <span className="ml-2 text-sm text-muted-foreground">
                          {STAR_LABELS[activeStars - 1]}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Comentário */}
                  <div>
                    <Label htmlFor="comment" className="text-sm font-medium mb-2 block">
                      Comentário
                    </Label>
                    <Textarea
                      id="comment"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="O que você achou do curso? Conte sua experiência..."
                      rows={4}
                      className="resize-none"
                    />
                    <p className="text-xs mt-3 text-muted-foreground">
                      {comment.trim().length >= 50
                        ? '✅'
                        : `Mínimo de 50 caracteres, você digitou ${comment.trim().length}`}
                    </p>
                  </div>

                  {/* Ações */}
                  <div className="flex flex-col gap-3 pt-2">
                    <Button
                      onClick={handleSubmit}
                      disabled={
                        rating === 0 || comment.trim().length < 50 || submitFeedback.isPending
                      }
                      className="gap-2 w-full"
                      size="lg"
                    >
                      {submitFeedback.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        'Enviar Feedback e Emitir Certificado'
                      )}
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      size="lg"
                      className="w-full hover:bg-muted/30 hover:text-muted-foreground"
                    >
                      <Link to="/">Avaliar depois</Link>
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
        <Footer />
      </div>
    </div>
  )
}

export default CourseFeedback
